// ABOUTME: Form component for creating a new workspace with company details.
// ABOUTME: Used in workspace management UI for adding additional workspaces beyond onboarding.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { trpc } from '@/lib/trpc';
import type { CreateWorkspaceRequest } from '@/types/workspace';

interface CreateWorkspaceFormProps {
    onSuccess?: (workspaceId: string) => void;
    onCancel?: () => void;
}

export function CreateWorkspaceForm({ onSuccess, onCancel }: CreateWorkspaceFormProps) {
    const navigate = useNavigate();
    const { refreshWorkspaces, switchWorkspace } = useWorkspace();

    const [formData, setFormData] = useState<CreateWorkspaceRequest>({
        name: '',
        companyName: '',
        businessEmail: '',
        businessAddress: '',
        gstCode: '',
    });
    const [error, setError] = useState<string | null>(null);

    const createWorkspaceMutation = trpc.workspace.create.useMutation();
    const loading = createWorkspaceMutation.isPending;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            setError('Workspace name is required');
            return;
        }

        setError(null);

        try {
            const workspace = await createWorkspaceMutation.mutateAsync({
                ...formData,
                name: formData.name.trim(),
            });

            // Refresh workspaces list and switch to the new workspace
            await refreshWorkspaces();
            await switchWorkspace(workspace.id);

            if (onSuccess) {
                onSuccess(workspace.id);
            } else {
                navigate('/app/workspace/settings');
            }
        } catch (err) {
            console.error('Failed to create workspace:', err);
            setError(err instanceof Error ? err.message : 'Failed to create workspace');
        }
    };

    return (
        <Card className="max-w-xl mx-auto">
            <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg bg-primary/10">
                        <Building2 className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                        <CardTitle>Create Workspace</CardTitle>
                        <CardDescription>
                            Set up a new workspace for your team
                        </CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                    {error && (
                        <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                            {error}
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="name">Workspace Name *</Label>
                        <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="e.g., My Company"
                            required
                            disabled={loading}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="companyName">Company Name</Label>
                        <Input
                            id="companyName"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleChange}
                            placeholder="Legal company name"
                            disabled={loading}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="businessEmail">Business Email</Label>
                        <Input
                            id="businessEmail"
                            name="businessEmail"
                            type="email"
                            value={formData.businessEmail}
                            onChange={handleChange}
                            placeholder="billing@company.com"
                            disabled={loading}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="businessAddress">Business Address</Label>
                        <Input
                            id="businessAddress"
                            name="businessAddress"
                            value={formData.businessAddress}
                            onChange={handleChange}
                            placeholder="Company address"
                            disabled={loading}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="gstCode">GST Code</Label>
                        <Input
                            id="gstCode"
                            name="gstCode"
                            value={formData.gstCode}
                            onChange={handleChange}
                            placeholder="GST number (if applicable)"
                            disabled={loading}
                        />
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                        {onCancel && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onCancel}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                        )}
                        <Button type="submit" disabled={loading || !formData.name.trim()}>
                            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Create Workspace
                        </Button>
                    </div>
                </form>
            </CardContent>
        </Card>
    );
}
